import { trpcVanilla } from "@client/trpc";
import { cn } from "@client/utils/cn";
import {
  type CreateRoundWizardMember,
  type CreateRoundWizardProposal,
  type CreateRoundWizardRound,
  stampActionTimestamp,
} from "@engine/round/actions";
import { useCurrentUser } from "@client/hooks/use-current-user";
import {
  generateActionId,
  generateProposalFormId,
  generateProposalId,
  generateRoundId,
} from "@lib/utils/idGeneration";
import { Check } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Redirect } from "wouter";
import { RoundWizardComplete } from "./steps/RoundWizardComplete";
import { RoundWizardMembers } from "./steps/RoundWizardMembers";
import { RoundWizardProposal } from "./steps/RoundWizardProposal";
import { RoundWizardRound } from "./steps/RoundWizardRound";

const steps = [
  { id: "round", title: "Configure Round" },
  { id: "proposal", title: "Add Proposal" },
  { id: "members", title: "Invite Team" },
  { id: "complete", title: "Complete" },
];

export type RoundWizardMembersData = (CreateRoundWizardMember & {
  email: string;
})[];

export function RoundWizard({ isSfcAdmin }: { isSfcAdmin: boolean }) {
  // Use the tRPC current-user source (bearer-aware) rather than the cookie-only
  // /api/session, so native iPad sessions (no cookie) resolve here too.
  const { data: session, isLoading: loading } = useCurrentUser();
  const roundIdRef = useRef<string>(generateRoundId());
  const [currentStep, setCurrentStep] = useState(0);
  const [roundData, setRoundData] = useState<CreateRoundWizardRound>({
    name: "",
    initials: "",
    budget: 0,
    isSfcRound: isSfcAdmin,
    usesTracks: true,
    roundIdentifier: undefined,
  });
  const [proposalData, setProposalData] = useState<CreateRoundWizardProposal>({
    proposalFormId: generateProposalFormId(),
    proposalId: generateProposalId(),
    shortname: "",
    longname: "",
    requestAmount: 0,
    customFields: [],
  });
  const [membersData, setMembersData] = useState<RoundWizardMembersData>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const progressSteps = roundData.isSfcRound ? [steps[0]] : steps.slice(0, -1);

  // Initialize members data when session loads
  useEffect(() => {
    if (session && membersData.length === 0) {
      setMembersData([
        {
          id: session.userId,
          email: session.email,
          initials: "",
          roles: ["admin", "funder"],
        },
      ]);
    }
  }, [session, membersData.length]);

  function updateRoundData(data: CreateRoundWizardRound) {
    setRoundData(data);
    setMembersData((prev) => {
      return prev.map((member) => {
        if (member.id === session?.userId) {
          return {
            ...member,
            initials: data.initials,
          };
        }
        return member;
      });
    });
  }

  const handleIsSfcRoundChange = useCallback((isSfcRound: boolean) => {
    setRoundData((prev) => {
      if (prev.isSfcRound === isSfcRound) {
        return prev;
      }
      return { ...prev, isSfcRound };
    });
  }, []);

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const getMembersForRound = (
    nextRoundData: CreateRoundWizardRound,
    sourceMembers: RoundWizardMembersData,
  ): RoundWizardMembersData => {
    if (sourceMembers.length === 0) {
      return [
        {
          id: session!.userId,
          email: session!.email,
          initials: nextRoundData.initials,
          roles: ["admin", "funder"],
        },
      ];
    }

    return sourceMembers.map((member) => {
      if (member.id !== session!.userId) {
        return member;
      }
      return {
        ...member,
        initials: nextRoundData.initials,
      };
    });
  };

  const handleSubmit = async (options?: {
    skipToComplete?: boolean;
    roundDataOverride?: CreateRoundWizardRound;
    membersDataOverride?: RoundWizardMembersData;
  }) => {
    if (isSubmitting) {
      return;
    }

    const effectiveRoundData = options?.roundDataOverride ?? roundData;
    const effectiveMembersData = options?.membersDataOverride ?? membersData;

    try {
      setIsSubmitting(true);
      await trpcVanilla.performRoundAction.mutate({
        aggregateId: roundIdRef.current,
        id: generateActionId(),
        underlyingUserId: session!.userId,
        actingAsUserId: session!.userId,
        undoes: null,
        redoes: null,
        payload: stampActionTimestamp({
          type: "CREATE_ROUND_WIZARD",
          round: effectiveRoundData,
          proposal: proposalData.shortname ? proposalData : undefined,
          members: effectiveMembersData.map((member) => ({
            ...member,
            deanonymize: true,
          })),
        }),
      });
      if (options?.skipToComplete) {
        setCurrentStep(steps.length - 1);
      } else {
        handleNext();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to create round");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!session) {
    return <Redirect to="/" />;
  }

  return (
    <div className="flex flex-col gap-8 overflow-hidden">
      <ol className="flex">
        {progressSteps.map((step, index) => (
          <li key={step.id} className="flex-1 flex flex-col gap-1 text-center">
            <div className="flex-1 flex gap-2 items-center">
              <div
                className={cn(
                  "flex-1 h-0.5",
                  index < currentStep && "bg-green-600",
                  index === currentStep && "bg-blue-600",
                  index > currentStep && "bg-border",
                  index === 0 && "bg-transparent",
                )}
              />
              <div
                className={cn(
                  "flex items-center justify-center size-10 rounded-full",
                  index < currentStep && "bg-green-600 text-white",
                  index === currentStep && "bg-blue-600 text-white",
                  index > currentStep && "bg-border text-muted-foreground",
                )}
              >
                {index < currentStep ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>
              <div
                className={cn(
                  "flex-1 h-0.5",
                  index < currentStep - 1 && "bg-green-600",
                  index === currentStep - 1 && "bg-blue-600",
                  index >= currentStep && "bg-border",
                  index === progressSteps.length - 1 && "bg-transparent",
                )}
              />
            </div>
            <span
              className={cn(
                "flex-1 text-sm font-medium",
                index > currentStep && "text-muted-foreground",
              )}
            >
              {step.title}
            </span>
          </li>
        ))}
      </ol>

      {currentStep === 0 && (
        <RoundWizardRound
          isSfcAdmin={isSfcAdmin}
          data={roundData}
          updateData={updateRoundData}
          onIsSfcRoundChange={handleIsSfcRoundChange}
          onNext={(nextRoundData) => {
            if (nextRoundData.isSfcRound) {
              const nextMembersData = getMembersForRound(
                nextRoundData,
                membersData,
              );
              setMembersData(nextMembersData);
              void handleSubmit({
                skipToComplete: true,
                roundDataOverride: nextRoundData,
                membersDataOverride: nextMembersData,
              });
              return;
            }
            handleNext();
          }}
        />
      )}
      {currentStep === 1 && (
        <RoundWizardProposal
          data={proposalData}
          updateData={setProposalData}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}
      {currentStep === 2 && (
        <RoundWizardMembers
          userId={session.userId}
          userInitials={roundData.initials}
          members={membersData}
          updateMembers={setMembersData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onBack={handleBack}
        />
      )}
      {currentStep === 3 && (
        <RoundWizardComplete
          roundId={roundIdRef.current}
          isSfcRound={roundData.isSfcRound}
        />
      )}
    </div>
  );
}
